import { useGetCustomersByIdsQuery, useGetCustomersQuery } from "@builder/store/slices/dataApiSlices/customersApiSlice";
import { useGetOrdersByIdsQuery, useGetOrdersQuery } from "@builder/store/slices/dataApiSlices/ordersApiSlice";
import { useGetProductsByIdsQuery, useGetProductsQuery } from "@builder/store/slices/dataApiSlices/productsApiSlice";
import { useGetRolesByIdsQuery, useGetRolesQuery } from "@builder/store/slices/dataApiSlices/rolesApiSlice";
import { useGetUsersByIdsQuery, useGetUsersQuery } from "@builder/store/slices/dataApiSlices/userApiSlice";
import { useAppSelector } from "@builder/store/store";
import { SOURCE_OPTIONS } from "@builder/utils/constants";
import { skipToken } from "@reduxjs/toolkit/query";

export interface ConditionDataQuery {
    limit: number;
    offset: number;
    search: string;
}

interface ConditionDataProps {
    query?: ConditionDataQuery;
    conditionSource?: SOURCE_OPTIONS;
    additionalDataIds?: string[];
    isOrg: boolean;
}

export const useGetConditionData = ({ query, conditionSource, additionalDataIds, isOrg }: ConditionDataProps) => {
    const company = useAppSelector(state => state.storeData.data?.company);
    // PRODUCT
    const isProducts = conditionSource === SOURCE_OPTIONS.ACTIVE_PRODUCT || conditionSource === SOURCE_OPTIONS.PRODUCT_IN_CART;
    const {
        data: products,
        isLoading: isLoadingProducts,
        isFetching: isFetchingProducts
    } = useGetProductsQuery(
        company?._id && isProducts ? { companyId: company._id, ...query, ...(isOrg && { organizationId: company.organizationId }) } : skipToken
    );
    const {
        data: productsByIds,
        isLoading: isLoadingProductsByIds,
        isFetching: isFetchingProductsByIds
    } = useGetProductsByIdsQuery(
        company && isProducts && additionalDataIds && additionalDataIds.length > 0
            ? { companyId: company?._id, ids: additionalDataIds, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );

    // USER
    const {
        data: users,
        isLoading: isLoadingUsers,
        isFetching: isFetchingUsers
    } = useGetUsersQuery(
        company?._id && conditionSource === SOURCE_OPTIONS.USER
            ? { companyId: company._id, ...query, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );
    const {
        data: usersByIds,
        isLoading: isLoadingUsersByIds,
        isFetching: isFetchingUsersByIds
    } = useGetUsersByIdsQuery(
        company && conditionSource === SOURCE_OPTIONS.USER && additionalDataIds && additionalDataIds.length > 0
            ? { companyId: company._id, ids: additionalDataIds, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );

    // ROLE
    const {
        data: roles,
        isLoading: isLoadingRoles,
        isFetching: isFetchingRoles
    } = useGetRolesQuery(
        company && conditionSource === SOURCE_OPTIONS.ROLE
            ? { companyId: company._id, ...query, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );
    const {
        data: rolesByIds,
        isLoading: isLoadingRolesByIds,
        isFetching: isFetchingRolesByIds
    } = useGetRolesByIdsQuery(
        company && conditionSource === SOURCE_OPTIONS.ROLE && additionalDataIds && additionalDataIds.length > 0
            ? { companyId: company._id, ids: additionalDataIds, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );

    // CUSTOMER
    const isCustomers = conditionSource === SOURCE_OPTIONS.ACTIVE_CUSTOMER || conditionSource === SOURCE_OPTIONS.CUSTOMER_IN_CART;
    const {
        data: customers,
        isLoading: isLoadingCustomers,
        isFetching: isFetchingCustomers
    } = useGetCustomersQuery(
        company && isCustomers ? { companyId: company._id, ...query, ...(isOrg && { organizationId: company.organizationId }) } : skipToken
    );
    const {
        data: customersByIds,
        isLoading: isLoadingCustomersByIds,
        isFetching: isFetchingCustomersByIds
    } = useGetCustomersByIdsQuery(
        company && isCustomers && additionalDataIds && additionalDataIds.length > 0
            ? { companyId: company._id, ids: additionalDataIds, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );

    // ORDER

    const {
        data: orders,
        isLoading: isLoadingOrders,
        isFetching: isFetchingOrders
    } = useGetOrdersQuery(
        company && conditionSource === SOURCE_OPTIONS.ACTIVE_ORDER
            ? { companyId: company._id, ...query, ...(isOrg && { organizationId: company.organizationId }) }
            : skipToken
    );
    const {
        data: ordersByIds,
        isLoading: isLoadingOrdersByIds,
        isFetching: isFetchingOrdersByIds
    } = useGetOrdersByIdsQuery(
        company && conditionSource === SOURCE_OPTIONS.ACTIVE_ORDER && additionalDataIds && additionalDataIds.length > 0
            ? {
                  companyId: company._id,
                  ids: additionalDataIds,
                  ...(isOrg && { organizationId: company.organizationId })
              }
            : skipToken
    );

    switch (conditionSource) {
        case SOURCE_OPTIONS.ACTIVE_PRODUCT:
        case SOURCE_OPTIONS.PRODUCT_IN_CART:
            return {
                data: products,
                dataByIds: productsByIds,
                isLoading: isLoadingProducts || isLoadingProductsByIds,
                isFetching: isFetchingProducts || isFetchingProductsByIds,
                hasMore: query?.limit ? products && products.length === query?.limit : products && products.length > 0
            };
        case SOURCE_OPTIONS.USER:
            return {
                data: users,
                dataByIds: usersByIds,
                isLoading: isLoadingUsers || isLoadingUsersByIds,
                isFetching: isFetchingUsers || isFetchingUsersByIds,
                hasMore: query?.limit ? users && users.length === query?.limit : users && users.length > 0
            };
        case SOURCE_OPTIONS.ROLE:
            return {
                data: roles,
                dataByIds: rolesByIds,
                isLoading: isLoadingRoles || isLoadingRolesByIds,
                isFetching: isFetchingRoles || isFetchingRolesByIds,
                hasMore: query?.limit ? roles && roles.length === query?.limit : roles && roles.length > 0
            };
        case SOURCE_OPTIONS.ACTIVE_CUSTOMER:
        case SOURCE_OPTIONS.CUSTOMER_IN_CART:
            return {
                data: customers,
                dataByIds: customersByIds,
                isLoading: isLoadingCustomers || isLoadingCustomersByIds,
                isFetching: isFetchingCustomers || isFetchingCustomersByIds,
                hasMore: query?.limit ? customers && customers.length === query?.limit : customers && customers.length > 0
            };
        case SOURCE_OPTIONS.ACTIVE_ORDER:
            return {
                data: orders,
                dataByIds: ordersByIds,
                isLoading: isLoadingOrders || isLoadingOrdersByIds,
                isFetching: isFetchingOrders || isFetchingOrdersByIds,
                hasMore: query?.limit ? orders && orders.length === query?.limit : orders && orders.length > 0
            };
        case SOURCE_OPTIONS.NEW_ORDER:
            return {
                data: [
                    { _id: "POS", name: "POS order" },
                    { _id: "Online", name: "Online order" }
                ],
                dataByIds: [],
                isLoading: false,
                isFetching: false,
                hasMore: false
            };
        default:
            return {
                data: [],
                dataByIds: [],
                isLoading: false,
                isFetching: false,
                hasMore: false
            };
    }
};
